/** @file dragReadout: a drag's label, the key value the release stores and its change (yaw, Δ). */
import type { NodeKey } from '@zilo/pose3d-format/types/clip';
import type { GizmoHandle } from './gizmoShape';

/** The gesture kinds a readout knows (the IK drag keys a translation: `move`). */
export type ReadoutKind = 'rotate' | 'move' | 'scale';

/** Ring X turns pitch, Y roll, Z yaw (design 19.6: Rotation X = pitch, Y = roll, Z = yaw); `rot` is yaw, pitch, roll. */
const RING_CHANNEL = [['pitch', 1], ['roll', 2], ['yaw', 0]] as const;
/** The free ring changes all three; they read in the key's order. */
const ALL_CHANNELS = [['yaw', 0], ['pitch', 1], ['roll', 2]] as const;

/** One decimal, never `-0.0`. */
const one = (v: number): string => (Math.round(v * 10) / 10 + 0).toFixed(1);
/** Two decimals, never `-0.00`. */
const two = (v: number): string => (Math.round(v * 100) / 100 + 0).toFixed(2);
/** `d` wrapped into [-180, 180). */
const wrap = (d: number): number => ((((d + 180) % 360) + 360) % 360) - 180;
/** `+12.5°` / `-3.0°`. */
const signed = (d: number): string => `${Math.round(d * 10) / 10 < 0 ? '-' : '+'}${one(Math.abs(d))}°`;

/** The rotation channels ring `axis` changes, each `name value° (Δ change°)`. */
function rotation(axis: GizmoHandle, start: NodeKey, key: NodeKey): string {
  const rot = key.rot ?? [0, 0, 0];
  const from = start.rot ?? [0, 0, 0];
  const channels = axis <= 2 ? [RING_CHANNEL[axis as 0 | 1 | 2]] : ALL_CHANNELS;
  return channels.map(([name, i]) => `${name} ${one(rot[i])}° (Δ ${signed(wrap(rot[i] - from[i]))})`).join('  ');
}

/**
 * What the release will store for the dragged joint: `rotate` the key angle the ring turns with its change (all three
 * on the free ring) and `· <step>°` while Ctrl snaps; `move` the key translation in units; `scale` `× <scale>`.
 */
export function dragReadout(kind: ReadoutKind, axis: GizmoHandle, start: NodeKey, key: NodeKey, snapDeg: number): string {
  switch (kind) {
    case 'rotate': {
      const text = rotation(axis, start, key);
      return snapDeg > 0 ? `${text} · ${String(snapDeg)}°` : text;
    }
    case 'move': {
      const p = key.pos ?? [0, 0, 0];
      return `pos ${two(p[0])}, ${two(p[1])}, ${two(p[2])} u`;
    }
    case 'scale':
      return `× ${two(key.scale ?? 1)}`;
  }
}
